import Animation from 'animation'
import * as scale from 'd3-scale'
import * as shape from 'd3-shape'
import { interpolate } from 'd3-interpolate'
import { path as d3Path } from 'd3-path'
import linePoints from 'line-points'
import * as d3 from 'd3-selection'
import * as colors from 'colors'

class RegressionAnimation extends Animation {

  configure (axis, serie) {
    super.configure(axis, serie)

    if (serie.fills.length && serie.fills.length !== 2) {
      throw new Error('Fills array length must equal 2 if defined.')
    }

    /*
     * Regression data points (honestly maybe just linear regression data,
     * unsure if other types will work w/ this approach) is set by Impact.
     * Impact needs to set the first point to be one period before the range
     * and the second point to one point after the last item in the range.
     *
     * We then determine the range of this scale by extending the group scale
     * by one range item in each direction
     */
    let domain = this.scalers.x.groupScale.domain()
      , step = this.scalers.x.groupScale.step()
      , x0 = this.scalers.x.center(domain.at(0))
      , x1 = this.scalers.x.center(domain.at(-1))

    this.scale = scale.scaleLinear()
                      .domain([serie.points[0][0], serie.points[serie.points.length - 1][0]])
                      .range([x0 - step, x1 + step])

    this.line = shape.line()
                     .x(d => this.scale(d[0]))
                     .y(d => this.yScale(d[1]))

    this.fill = {
      bottom: shape.area()
                   .x(d => this.scale(d[0]))
                   .y0(d => Math.max(this.yScale.range()[0], this.yScale(d[1])))
                   .y1(d => this.yScale(d[1])),
      top: shape.area()
                .x(d => this.scale(d[0]))
                .y0(this.yScale.range()[1])
                .y1(d => this.yScale(d[1]))
    }

    // Area path without substance. Used for entering/exiting fills
    this.flatArea = shape.area()
                         .x(d => this.scale(d[0]))
                         .y0(d => this.yScale(d[1]))
                         .y1(d => this.yScale(d[1]))

    this._insertFills = this._insertFills.bind(this)
    this._growFills = this._growFills.bind(this)

    this._lineInterpolater = this._getLineInterpolater(serie)

    return this
  }

  /*
   * Returns a boolean indicating if the regression has either a top
   * or bottom fill.
   */
  _hasFills (serie) {
    return !!(serie.fills || []).filter(Boolean).length
  }

  _getLineInterpolater (serie, nodes) {
    let points = linePoints(this.line(serie.points))
      , interpolater = interpolate(0, points.length)
      , series = this.series.selection()

    return t => {
      let path = d3Path()
        , idx = Number.parseInt(interpolater(t))

      path.moveTo(points[0][0], points[0][1])
      points.slice(0, idx).forEach(p => path.lineTo(p[0], p[1]))
      nodes.attr('d', path.toString())
    }
  }

  _growFills (transition) {
    let self = this
    transition.selectAll('path.fill')
              .tween('grow', function (d) {
                let interpolater = interpolate(this.getAttribute('d'), self.fill[this.dataset.fill](d.points))
                return t => {
                  this.setAttribute('d', interpolater(t))
                }
              })
  }

  _insertFills (selection, transition) {
    selection.append('path')
             .attr('data-fill', d => d.type)
             .attr('class', d => `fill ${d.color}`)
             .style('opacity', d => d.opacity)
             .attr('d', d => this.flatArea(d.points))
  }

  _fillData (serie) {
    let fills = (serie.fills || [undefined, undefined])
      , result = []

    if (fills[0]) {
      result.push({
        type: 'top',
        color: fills[0] ? fills[0].color : '',
        opacity: fills[0] ? fills[0].opacity : 1,
        points: serie.points
      })
    }

    if (fills[1]) {
      result.push({
        type: 'bottom',
        color: fills[1] ? fills[1].color : '',
        opacity: fills[1] ? fills[1].opacity : 1,
        points: serie.points
      })
    }

    return result
  }

  bindAndAnimate (container, node) {
    let serie = d3.select(node).datum()
      , svg = d3.select(node.ownerSVGElement)
      , group = d3.select(node)
                  .selectAll(`.regression-group`)
                  .data(d => [d])
      , enter = group.enter()
                     .append('g')
                     .attr('class', `.regression-group`)
      , transition = group.merge(enter)
                          .attr('class', `regression-group`)
                          .attr('clip-path', `url(#${svg.attr('id')}-chart-clip-path-plot-width-height-bufferless-${this.plotId})`)
                          .transition(container)
      , exit = this.exitingSeries.filter(exiting => exiting.id === serie.id)

    this.animate(serie, enter, group, transition, exit)
  }

  _setStroke (selection, color) {
    selection.style('stroke', color ? colors.lookup(color) : null)
  }

  _setFill (selection, color) {
    selection.style('fill', color ? colors.lookup(color) : null)
  }

}

export default RegressionAnimation
